import type { MantineTheme } from '@mantine/core';
import { ActionIcon, createStyles, Group, Menu, Tooltip } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { Icon } from '@repo/foundations';
import { typography } from '@repo/theme/primitives';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useNavigate } from '../../hooks/useNavigate';
import { pickColor } from '../../utils';
import type { ItemIconType } from '../ItemIcon';
import type { SelectablePropertyItem } from '../SingleSelector/types';
import SelectorItemCheckbox from './SelectorItemCheckbox';
import SelectorItemContent from './SelectorItemContent';

export interface IMultiSelectorItemProps {
	iconType: ItemIconType;
	item: SelectablePropertyItem;
	isMenuItemBadge: boolean;
	isViewerUser: boolean;
	permittedId: string;
	onClick: (item: SelectablePropertyItem) => void;
	checked?: boolean;
	disabled?: boolean;
	disabledTooltip?: string;
	isChromeUser?: boolean;
}

const useStyles = createStyles((theme: MantineTheme) => ({
	badge: {
		backgroundColor: theme.other.getColor('fill/transparent-secondary/default'),
	},
	badgeInner: {
		fontSize: theme.fontSizes.sm,
		fontWeight: typography.weight.regular,
		color: theme.other.getColor('text/primary/default'),
		lineHeight: '20px',
	},
	actionIcon: {
		backgroundColor: theme.other.getColor('fill/transparent/default'),
		width: 20,
		height: 20,
		padding: 0,
		'&:hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
		},
		'&:active': {
			backgroundColor: theme.other.getColor('fill/transparent/active'),
		},
	},
}));

function MultiSelectorItem({
	iconType,
	item,
	isMenuItemBadge,
	isViewerUser,
	permittedId,
	onClick,
	checked = false,
	disabled,
	disabledTooltip,
	isChromeUser,
}: IMultiSelectorItemProps) {
	const { hovered, ref } = useHover<HTMLButtonElement>();

	const { theme, classes } = useStyles();

	const navigate = useNavigate();

	let color: string;

	if (iconType === 'avatar') {
		color = pickColor(item.label);
	} else if (iconType === 'tag') {
		color = theme.other.getColor('icon/brand/default');
	} else {
		color =
			(item.color as string) || theme.other.getColor('icon/brand/default');
	}

	const RightSection = useMemo(
		() =>
			(!isViewerUser || isChromeUser || permittedId === item.value) &&
			(!isNil(item.navigateTo) || !isNil(item.onNavigate)) &&
			hovered ? (
				<ActionIcon
					className={classes.actionIcon}
					size={20}
					onClick={(event) => {
						event.stopPropagation();
						if (item.onNavigate) {
							item.onNavigate();
						} else {
							navigate(item.navigateTo as string);
						}
					}}
				>
					<Icon name="chevronRight" color="icon/primary/default" />
				</ActionIcon>
			) : null,
		[
			isViewerUser,
			hovered,
			classes.actionIcon,
			permittedId,
			navigate,
			item,
			isChromeUser,
		]
	);

	const handleOnClick = () => {
		if (isViewerUser && !isNil(item.navigateTo) && permittedId !== item.value) {
			navigate(item.navigateTo);
		} else if (!disabled) {
			onClick(item);
		}
	};

	return (
		<Menu.Item
			ref={ref}
			icon={
				<SelectorItemCheckbox
					checked={checked}
					disabled={!!disabled}
					disabledTooltip={disabledTooltip}
					item={item}
					isViewerUser={isViewerUser}
					permittedId={permittedId}
				/>
			}
			rightSection={RightSection}
			onClick={handleOnClick}
		>
			<Group spacing={4} noWrap>
				{item.showDescriptionInTooltip && item.description ? (
					<Tooltip label={item.description} withinPortal>
						<SelectorItemContent
							isMenuItemBadge={isMenuItemBadge}
							iconType={iconType}
							item={item}
							color={color}
						/>
					</Tooltip>
				) : (
					<SelectorItemContent
						isMenuItemBadge={isMenuItemBadge}
						iconType={iconType}
						item={item}
						color={color}
					/>
				)}
			</Group>
		</Menu.Item>
	);
}

export default MultiSelectorItem;
